import { useState, useCallback, useRef, useEffect } from 'react';
import { useBusinessAuth } from '../context/BusinessAuthContext';
import {
  getNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead,
} from '../services/businessNotificationService';

/**
 * useBusinessNotifications – notifications hook for the Business portal.
 *
 * Provides the same interface as the base-user useNotifications hook
 * but uses business-portal API endpoints (X-Portal: business header).
 */
export default function useBusinessNotifications() {
  const { isAuthenticated } = useBusinessAuth();

  const [items, setItems]           = useState([]);
  const [meta, setMeta]             = useState({});
  const [loading, setLoading]       = useState(false);
  const [unreadCount, setUnread]    = useState(0);
  const [filter, setFilter]         = useState('all');
  const [category, setCategory]     = useState('');
  const pageRef   = useRef(1);
  const hasMore   = useRef(true);
  const mountedRef = useRef(true);

  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  const fetchPage = useCallback(async (page, append = false) => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const params = { page, limit: 20, filter };
      if (category) params.category = category;

      const result = await getNotifications(params);
      if (!mountedRef.current) return;

      const newItems  = result.items || result.data || [];
      const pageMeta  = result.meta  || {};

      setItems(prev => append ? [...prev, ...newItems] : newItems);
      setMeta(pageMeta);
      hasMore.current = page < (pageMeta.totalPages || 1);
    } catch (err) {
      console.error('[useBusinessNotifications] fetch error', err);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [isAuthenticated, filter, category]);

  useEffect(() => {
    if (!isAuthenticated) { setItems([]); setUnread(0); return; }
    pageRef.current = 1;
    hasMore.current = true;
    fetchPage(1);
  }, [fetchPage, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    getUnreadCount().then(c => mountedRef.current && setUnread(c)).catch(() => {});
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const poll = () => {
      if (document.visibilityState !== 'visible') return;
      getUnreadCount().then(c => mountedRef.current && setUnread(c)).catch(() => {});
      if (pageRef.current === 1) fetchPage(1);
    };
    const onVisibility = () => { if (document.visibilityState === 'visible') poll(); };
    document.addEventListener('visibilitychange', onVisibility);
    const timer = setInterval(poll, 30_000);
    return () => { document.removeEventListener('visibilitychange', onVisibility); clearInterval(timer); };
  }, [fetchPage, isAuthenticated]);

  const loadMore = useCallback(() => {
    if (loading || !hasMore.current) return;
    pageRef.current += 1;
    fetchPage(pageRef.current, true);
  }, [loading, fetchPage]);

  const refresh = useCallback(() => {
    pageRef.current = 1;
    hasMore.current = true;
    fetchPage(1);
    getUnreadCount().then(c => mountedRef.current && setUnread(c)).catch(() => {});
  }, [fetchPage]);

  const handleMarkRead = useCallback(async (id) => {
    try {
      await markAsRead(id);
      setItems(prev => prev.map(n => n.id === id ? { ...n, isRead: true, is_read: true } : n));
      const freshCount = await getUnreadCount();
      if (mountedRef.current) setUnread(freshCount);
    } catch (err) {
      if (err?.response?.status === 404) {
        setItems(prev => prev.map(n => n.id === id ? { ...n, isRead: true, is_read: true } : n));
      }
      getUnreadCount().then(c => mountedRef.current && setUnread(c)).catch(() => {});
    }
  }, []);

  const handleMarkAllRead = useCallback(async () => {
    try {
      await markAllAsRead();
      setItems(prev => prev.map(n => ({ ...n, isRead: true, is_read: true })));
      const freshCount = await getUnreadCount();
      if (mountedRef.current) setUnread(freshCount);
    } catch {
      getUnreadCount().then(c => mountedRef.current && setUnread(c)).catch(() => {});
    }
  }, []);

  return {
    items,
    meta,
    loading,
    hasMore: hasMore.current,
    unreadCount,
    filter,
    setFilter,
    category,
    setCategory,
    loadMore,
    refresh,
    handleMarkRead,
    handleMarkAllRead,
    setUnreadCount: setUnread,
  };
}
